import { useMutation, useQuery } from '@apollo/client';
import { Col, notification, Row, Skeleton } from 'antd';
import Text from 'antd/lib/typography/Text';
import { format } from 'date-fns';
import { useNavigate, useParams } from 'react-router-dom';
import { CustomNotification } from 'src/components/custom-notification';
import { ROUTES } from 'src/config/route';

import { DeleteSlider, UpdateSlider } from 'src/lib/gql/mutations';
import { QuerySlider } from 'src/lib/gql/queries';
import SliderCard from '../../components/ui/sliders/slider-card';
import {
	CmsDeleteSliderMutationMutation,
	CmsDeleteSliderMutationMutationVariables,
	CmsGetSliderQuery,
	CmsGetSliderQueryVariables,
	CmsUpdateSliderMutationMutation,
	CmsUpdateSliderMutationMutationVariables,
	MediaType,
} from 'src/lib/generated/_generated';

const EditSlider = () => {
	interface Params {
		id: string;
		[key: string]: string | undefined;
	}
	const params = useParams<Params>();
	const navigate = useNavigate();
	const DEFAULT_TEXT =
		'Default Slide (Used as a backup in case Sliders expired and no image is displayed on Website)';

	const {
		data: qryData,
		loading: qryLoading,
		refetch: refetchQuery,
	} = useQuery<CmsGetSliderQuery, CmsGetSliderQueryVariables>(QuerySlider, {
		variables: {
			id: params.id ?? '',
		},
		fetchPolicy: 'cache-and-network',
	});

	const [updateSlider] = useMutation<
		CmsUpdateSliderMutationMutation,
		CmsUpdateSliderMutationMutationVariables
	>(UpdateSlider, {
		onError(error) {
			CustomNotification({
				pageName: 'Sliders',
				pagePrefix: 'Slider',
				notificationType: 'custom-error',
				customDescription: error.message,
			});
		},
	});

	const [deleteSlider] = useMutation<
		CmsDeleteSliderMutationMutation,
		CmsDeleteSliderMutationMutationVariables
	>(DeleteSlider, {
		onError(error) {
			CustomNotification({
				pageName: 'Sliders',
				pagePrefix: 'Slider',
				notificationType: 'custom-error',
				customDescription: error.message,
			});
		},
	});

	return (
		<Row>
			<Col span={24}>
				<Text className="sub-header">
					{qryData?.getSlider?.isDefault
						? DEFAULT_TEXT
						: 'Edit Slider '}
				</Text>
			</Col>
			{qryLoading ? (
				<Skeleton active />
			) : (
				<SliderCard
					inclSliderPosition={!qryData?.getSlider?.isDefault}
					sliderName={qryData?.getSlider?.title || ''}
					sliderSubtitle={qryData?.getSlider?.subtitle || ''}
					sliderButtonText={qryData?.getSlider?.buttonText || ''}
					sliderPosition={qryData?.getSlider?.weight || undefined}
					sliderVenueId={qryData?.getSlider?.venue?.id || undefined}
					sliderLink={qryData?.getSlider?.link || ''}
					canDelete={true}
					sliderDates={
						qryData?.getSlider?.sliderDates?.map((date) => date?.date || '') ||
						[]
					}
					sliderTags={qryData?.getSlider?.sliderHTags?.map(
						(tag) => tag?.hTag || '',
					)}
					sliderMedia={{
						id: qryData?.getSlider?.media?.id || '-1',
						url: qryData?.getSlider?.media?.url || '-1',
						type: qryData?.getSlider?.media?.type || MediaType.Image,
						...(qryData?.getSlider?.media?.thumbnail && {
							thumbnail: {
								id: qryData?.getSlider?.media?.thumbnail.id || '-1',
								url: qryData?.getSlider?.media?.thumbnail.url || '',
							},
						}),
					}}
					sliderMobileMedia={
						qryData?.getSlider?.mobileMedia
							? {
									id: qryData.getSlider.mobileMedia.id,
									url: qryData.getSlider.mobileMedia.url,
									type: MediaType.Image,
							  }
							: undefined
					}
					onSave={async (data) => {
						notification['info']({
							message: 'Sliders',
							description: 'Slider is updating...',
							duration: 2,
						});
						const rsp = await updateSlider({
							variables: {
								sliderDates:
									data.dates.map((date) => format(date, 'yyyy-MM-dd')) || [],
								sliderHTags: data.tags || [],
								mediaId: data.mediaId,
								venueId: data.venueId,
								weight: data.position,
								link: data.link,
								mediaType: data.mediaType,
								title: data.name || '',
								subtitle: data.subtitle || '',
								buttonText: data.buttonText || 'Find out more',
								videoLink: data.videoLink,
								mobileMediaId: data.mobileMediaId,
								id: params.id ?? '',
							},
						});
						if (rsp.data) {
							CustomNotification({
								pageName: 'Sliders',
								pagePrefix: 'Slider',
								notificationType: 'updated',
							});
						}
						refetchQuery();
					}}
					onDelete={async () => {
						if (!confirm('Are you sure you want to delete?')) return;

						const rsp = await deleteSlider({
							variables: {
								id: params.id ?? '', // Provide a default value using the nullish coalescing operator
							},
						});

						if (rsp.data) {
							CustomNotification({
								pageName: 'Sliders',
								pagePrefix: 'Slider',
								notificationType: 'deleted',
							});
						}

						navigate(`${ROUTES.SLIDERS.path}`);
					}}
				/>
			)}
			{qryData?.getSlider?.isDefault
				? DEFAULT_TEXT
				: ''}
		</Row>
	);
};

export default EditSlider;
